import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';
import { useLang } from '../hooks/useLang';
import './PostRequest.css';

const GERMAN_CITIES = ['Berlin', 'Hamburg', 'Munich', 'Frankfurt', 'Cologne', 'Stuttgart', 'Düsseldorf', 'Dortmund', 'Essen', 'Leipzig', 'Bremen', 'Dresden', 'Hanover', 'Nuremberg'];
const TUNISIAN_CITIES = ['Tunis', 'Sfax', 'Sousse', 'Monastir', 'Bizerte', 'Gabès', 'Ariana', 'Gafsa', 'Kairouan', 'Nabeul'];

export default function PostRequest() {
  const { user, profile } = useAuth();
  const navigate = useNavigate();
  const { t } = useLang();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [direction, setDirection] = useState('de_to_tn');
  const [form, setForm] = useState({
    origin_city: '',
    destination_city: '',
    needed_by_date: '',
    kg_needed: '',
    description: '',
    budget_per_kg: '',
    notes: '',
    recipient_name: '',
    recipient_phone: '',
  });
  const [includeDocuments, setIncludeDocuments] = useState(false);
  const [documentType,     setDocumentType]     = useState('official');
  const [documentCount,    setDocumentCount]     = useState('1');

  const isDeToTn = direction === 'de_to_tn';
  const originCities = isDeToTn ? GERMAN_CITIES : TUNISIAN_CITIES;
  const destCities = isDeToTn ? TUNISIAN_CITIES : GERMAN_CITIES;
  const originCountry = isDeToTn ? 'Germany' : 'Tunisia';
  const destCountry = isDeToTn ? 'Tunisia' : 'Germany';

  function update(f, v) { setForm(prev => ({ ...prev, [f]: v })); }

  const today = new Date().toISOString().split('T')[0];

  async function handleSubmit(e) {
    e.preventDefault();
    if (!user) { navigate('/login'); return; }
    if (!form.origin_city || !form.destination_city) { setError('Please select origin and destination cities.'); return; }
    if (!form.needed_by_date) { setError('Please select a needed-by date.'); return; }
    const hasKg   = parseFloat(form.kg_needed) > 0;
    const hasDocs = includeDocuments && parseInt(documentCount) > 0;
    if (!hasKg && !hasDocs) { setError('Please add at least some kg of goods OR at least one document.'); return; }
    if (includeDocuments && !hasDocs) { setError('Please enter the number of documents.'); return; }
    if (hasKg && !form.description.trim()) { setError('Please describe the goods you are sending.'); return; }
    if (!form.recipient_name.trim()) { setError('Please enter the recipient\'s name.'); return; }
    if (!form.recipient_phone.trim()) { setError('Please enter the recipient\'s phone number.'); return; }

    setLoading(true);
    setError('');

    const { error: insertError } = await supabase.from('shipment_requests').insert({
      sender_id: user.id,
      origin_city: form.origin_city,
      origin_country: originCountry,
      destination_city: form.destination_city,
      destination_country: destCountry,
      needed_by_date: form.needed_by_date,
      kg_needed: parseFloat(form.kg_needed) || 0,
      description: form.description,
      needs_documents: includeDocuments,
      document_type:   includeDocuments ? documentType : null,
      document_count:  includeDocuments ? parseInt(documentCount) : null,
      budget_per_kg: form.budget_per_kg ? parseFloat(form.budget_per_kg) : null,
      notes: form.notes,
      recipient_name: form.recipient_name,
      recipient_phone: form.recipient_phone,
      status: 'open',
    });

    setLoading(false);

    if (insertError) {
      setError(insertError.message);
    } else {
      navigate('/requests?posted=1');
    }
  }

  return (
    <div className="page post-request-page">
      <div className="container">
        <Link to="/requests" className="back-link">← Browse requests</Link>

        <div className="post-request-header">
          <h1>Post a Shipment Request</h1>
          <p>Tell transporters what you need to send. They'll reach out if they can help.</p>
        </div>

        <div className="post-request-layout">
          <form className="card post-request-form" onSubmit={handleSubmit}>

            {/* Direction */}
            <div className="form-group">
              <label>Direction</label>
              <div className="dir-toggle">
                <button type="button" className={`dir-btn ${direction === 'de_to_tn' ? 'active' : ''}`} onClick={() => { setDirection('de_to_tn'); update('origin_city', ''); update('destination_city', ''); }}>
                  🇩🇪 Germany → 🇹🇳 Tunisia
                </button>
                <button type="button" className={`dir-btn ${direction === 'tn_to_de' ? 'active' : ''}`} onClick={() => { setDirection('tn_to_de'); update('origin_city', ''); update('destination_city', ''); }}>
                  🇹🇳 Tunisia → 🇩🇪 Germany
                </button>
              </div>
            </div>

            {/* Cities */}
            <div className="grid-2">
              <div className="form-group">
                <label>From</label>
                <select value={form.origin_city} onChange={e => update('origin_city', e.target.value)} required>
                  <option value="">Select city</option>
                  {originCities.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>To</label>
                <select value={form.destination_city} onChange={e => update('destination_city', e.target.value)} required>
                  <option value="">Select city</option>
                  {destCities.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>

            {/* Needed by date */}
            <div className="form-group">
              <label>Needed by</label>
              <input type="date" min={today} value={form.needed_by_date} onChange={e => update('needed_by_date', e.target.value)} required />
            </div>

            {/* What are you sending */}
            <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '12px', padding: '16px', marginBottom: '16px' }}>
              <div style={{ fontWeight: '700', fontSize: '14px', color: 'var(--sand)', marginBottom: '12px' }}>What are you sending?</div>

              {/* KG goods */}
              <div className="form-group" style={{ marginBottom: '12px' }}>
                <label style={{ fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  📦 Goods (kg){' '}
                  <span style={{ fontWeight: '400', color: 'var(--olive)', textTransform: 'none', letterSpacing: 0 }}>— leave 0 if documents only</span>
                </label>
                <input
                  type="number" min="0" step="0.5" placeholder="e.g. 5"
                  value={form.kg_needed}
                  onChange={e => update('kg_needed', e.target.value)}
                  style={{ maxWidth: '160px' }}
                />
              </div>

              {/* Documents */}
              <div
                onClick={() => setIncludeDocuments(v => !v)}
                style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', padding: '10px 12px', borderRadius: '10px', background: includeDocuments ? 'rgba(76,175,125,0.08)' : 'transparent', border: `1px solid ${includeDocuments ? 'rgba(76,175,125,0.25)' : 'rgba(255,255,255,0.07)'}`, marginBottom: includeDocuments ? '12px' : '0', userSelect: 'none' }}
              >
                <div style={{ width: '20px', height: '20px', borderRadius: '4px', backgroundColor: includeDocuments ? '#4CAF7D' : 'transparent', border: `2px solid ${includeDocuments ? '#4CAF7D' : 'rgba(255,255,255,0.25)'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  {includeDocuments && <span style={{ color: '#fff', fontSize: '12px', lineHeight: 1 }}>✓</span>}
                </div>
                <span style={{ fontWeight: '600', fontSize: '13px', color: 'var(--sand)' }}>📄 I also need to send documents / envelopes</span>
              </div>

              {includeDocuments && (
                <div style={{ paddingLeft: '12px', borderLeft: '2px solid rgba(76,175,125,0.3)' }}>
                  <div className="form-group" style={{ marginBottom: '10px' }}>
                    <label>Document type</label>
                    <select value={documentType} onChange={e => setDocumentType(e.target.value)}>
                      <option value="official">🏛️ Official document (passport, visa, diploma)</option>
                      <option value="business">💼 Business document (contract, invoice)</option>
                      <option value="letter">✉️ Personal letter / envelope</option>
                      <option value="small_parcel">📦 Small parcel (under 500g)</option>
                    </select>
                  </div>
                  <div className="form-group" style={{ marginBottom: '0' }}>
                    <label>Number of items</label>
                    <input type="number" min="1" max="20" value={documentCount} onChange={e => setDocumentCount(e.target.value)} style={{ maxWidth: '100px' }} />
                  </div>
                </div>
              )}
            </div>

            {/* Description — only shown when sending goods */}
            {(parseFloat(form.kg_needed) > 0 || !includeDocuments) && (
              <div className="form-group">
                <label>Describe the goods</label>
                <textarea
                  placeholder="e.g. Clothes and shoes for family, some spices and olive oil from Tunisia..."
                  rows={3}
                  value={form.description}
                  onChange={e => update('description', e.target.value)}
                  required={parseFloat(form.kg_needed) > 0 || !includeDocuments}
                  maxLength={500}
                />
                <span style={{ fontSize: '11px', color: 'var(--olive)' }}>{form.description.length}/500</span>
              </div>
            )}

            {/* Recipient */}
            <div style={{ borderTop: '1px solid var(--border)', paddingTop: '16px', marginTop: '4px' }}>
              <div style={{ fontSize: '13px', fontWeight: '700', color: 'var(--sand)', marginBottom: '12px' }}>
                👤 Recipient details
                <span style={{ fontWeight: '400', color: 'var(--olive)', marginLeft: '8px', fontSize: '12px' }}>Who will receive the shipment?</span>
              </div>
              <div className="grid-2">
                <div className="form-group">
                  <label>Recipient name</label>
                  <input
                    type="text"
                    placeholder="Full name"
                    value={form.recipient_name}
                    onChange={e => update('recipient_name', e.target.value)}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Recipient phone</label>
                  <input
                    type="tel"
                    placeholder="+216 XX XXX XXX"
                    value={form.recipient_phone}
                    onChange={e => update('recipient_phone', e.target.value)}
                    required
                  />
                </div>
              </div>
            </div>

            {/* Budget */}
            <div className="form-group">
              <label>Your budget per kg <span style={{ color: 'var(--olive)', fontWeight: '400', fontSize: '12px' }}>— optional</span></label>
              <div style={{ position: 'relative' }}>
                <span style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: 'var(--olive)' }}>€</span>
                <input type="number" min="0" step="0.5" placeholder="0.00" value={form.budget_per_kg} onChange={e => update('budget_per_kg', e.target.value)} style={{ paddingLeft: '28px' }} />
              </div>
            </div>

            {/* Notes */}
            <div className="form-group">
              <label>Additional notes <span style={{ color: 'var(--olive)', fontWeight: '400', fontSize: '12px' }}>— optional</span></label>
              <textarea placeholder="Any specific requirements, fragile items, timing flexibility..." rows={2} value={form.notes} onChange={e => update('notes', e.target.value)} maxLength={300} />
            </div>

            {error && <div className="alert alert-error">{error}</div>}

            <button type="submit" className="btn btn-primary" disabled={loading} style={{ width: '100%' }}>
              {loading ? 'Posting...' : 'Post Shipment Request'}
            </button>
          </form>

          {/* Sidebar */}
          <div className="post-request-sidebar">
            <div className="card" style={{ padding: '20px' }}>
              <h3 style={{ fontFamily: 'Syne', fontSize: '15px', fontWeight: '700', marginBottom: '14px' }}>How it works</h3>
              {[
                { icon: '📝', text: 'Post your request — what you need shipped, when, and where' },
                { icon: '✈️', text: 'Transporters browse open requests and propose if they can help' },
                { icon: '💳', text: 'Accept a proposal and pay securely through the platform' },
                { icon: '🎉', text: 'Your goods get delivered to the recipient!' },
              ].map((s, i) => (
                <div key={i} style={{ display: 'flex', gap: '12px', alignItems: 'flex-start', marginBottom: '14px' }}>
                  <div style={{ fontSize: '18px', flexShrink: 0, width: '28px', textAlign: 'center' }}>{s.icon}</div>
                  <div style={{ fontSize: '13px', color: 'rgba(245,237,216,0.75)', lineHeight: '1.5' }}>{s.text}</div>
                </div>
              ))}
            </div>

            <div className="card" style={{ padding: '16px 20px', marginTop: '12px', borderColor: 'rgba(232,197,71,0.2)', background: 'rgba(232,197,71,0.05)' }}>
              <div style={{ fontSize: '13px', color: 'rgba(232,197,71,0.85)', lineHeight: '1.5' }}>
                💡 <strong>Tip:</strong> Including a budget and clear item description gets you more responses from transporters.
              </div>
            </div>

            <div style={{ marginTop: '16px' }}>
              <Link to="/customs" style={{ fontSize: '13px', color: 'var(--olive)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                🛃 Check the customs guide before posting →
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}